import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Space, Typography, Select, Spin, Empty } from 'antd';
import debounce from 'lodash/debounce';
// import { GET_USERS } from 'gql/users';
import { displayFacDept } from 'utils';
import { useQueryParams } from 'hooks';

const { Text } = Typography;
const { Option } = Select;
const GET_USERS = gql`
  query GET_USERS_FOR_SEARCH_TEAM_MEMBER(
    $ids: [ID!]
    $state: State
    $netIds: [String!]
    $departments: [ID!]
    $keyword: String
    $first: Int
  ) {
    users(
      ids: $ids
      state: $state
      netIds: $netIds
      departments: $departments
      keyword: $keyword
      first: $first
    ) {
      id
      netId
      title
      name
      faculty {
        id
        name
        abbreviation
      }
      department {
        id
        name
        faculty {
          id
          name
        }
      }
    }
  }
`;

const DebounceSelect = ({
  initialOptions,
  fetchOptions,
  debounceTimeout = 800,
  ...props
}) => {
  const [loading, setLoading] = useState();
  const [options, setOptions] = useState(initialOptions);
  const [keyword, setKeyword] = useState('');
  const [cache, setCache] = useState({});
  const fetchRef = React.useRef(0);
  const debounceFetcher = React.useMemo(() => {
    const loadOptions = async (value) => {
      setOptions([]);
      setKeyword(value);
      if (!value) {
        return;
      }
      if (cache[value]) {
        setOptions(cache[value]);
        console.log('get from cache');
        return;
      }
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setLoading(true);
      setOptions([]);
      const result = await fetchOptions(value);
      if (fetchId !== fetchRef.current) {
        // for fetch callback order
        return;
      }
      setCache((prev) => ({ ...prev, [value]: result.data?.users }));
      setOptions(result.data?.users ?? []);
      setLoading(false);
    };

    return debounce(loadOptions, debounceTimeout);
  }, [cache, fetchOptions, debounceTimeout]);

  return (
    <Select
      filterOption={false}
      onSearch={debounceFetcher}
      onClear={() => {
        setKeyword('');
        setOptions([]);
      }}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : (
          <Empty
            className="ant-empty-small"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={keyword ? 'No User Found' : 'Type to Search'}
          />
        )
      }
      {...props}
    >
      {options?.map(({ id, name, netId, department, faculty }) => (
        <Option key={id} value={id} label={`${name} (${netId})`}>
          <Space direction="vertical">
            {`${name} (${netId})`}
            <Text type="secondary">
              {displayFacDept({
                department: department,
                faculty: faculty,
                prefix: false,
              })}
            </Text>
          </Space>
        </Option>
      ))}
    </Select>
  );
};

const ProTableSearchTeamMember = (props) => {
  let queryParams = useQueryParams();
  const query = {
    teamMember: queryParams.get('teamMember'),
  };
  const { data, refetch, loading } = useQuery(GET_USERS, {
    variables: { ids: query.teamMember ? [query.teamMember] : [], first: 10 },
  });

  if (loading) return <Spin />;

  return (
    <DebounceSelect
      showSearch
      allowClear
      value={props.value}
      placeholder="Please select"
      onChange={props.onChange}
      debounceTimeout={300}
      optionLabelProp="label"
      fetchOptions={async (keyword) => {
        return refetch({ ids: [], keyword, first: 10 });
      }}
      initialOptions={data?.users}
    ></DebounceSelect>
  );
};

export default ProTableSearchTeamMember;
